// src/components/admin/Support.jsx
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { toast } from 'react-toastify';
import {
  FaPlus, FaInbox, FaSpinner, FaCheckCircle, FaClock,
  FaBoxes, FaImage, FaTag, FaLock, FaCreditCard, FaGlobe,
  FaExclamationTriangle, FaArrowUp, FaQuestionCircle,
  FaSearch, FaSync, FaEye, FaTimes, FaPaperclip, FaPaperPlane
} from 'react-icons/fa';
import './Support.css';

const REQUEST_TYPES = [
  { value: 'add_more_products', label: 'Add more products', desc: 'Request more than your current product limit.', icon: <FaBoxes /> },
  { value: 'change_banner', label: 'Change banner', desc: 'Ask our team to update a homepage banner.', icon: <FaImage /> },
  { value: 'add_new_category', label: 'Add new category', desc: 'Add a new product category to your store.', icon: <FaTag /> },
  { value: 'store_category_change', label: 'Store category change', desc: 'Use this if your business category has changed and you need our team to review your store setup.', icon: <FaLock /> },
  { value: 'payment_issue', label: 'Payment issue', desc: 'Report payment link or billing problems.', icon: <FaCreditCard /> },
  { value: 'domain_issue', label: 'Domain issue', desc: 'Get help connecting or fixing your domain.', icon: <FaGlobe /> },
  { value: 'store_not_working', label: 'Store not working', desc: 'Report checkout, loading, or display issues.', icon: <FaExclamationTriangle /> },
  { value: 'plan_upgrade', label: 'Plan upgrade', desc: 'Move to a bigger plan with more features.', icon: <FaArrowUp /> },
  { value: 'other_request', label: 'Other request', desc: 'Anything else you need help with.', icon: <FaQuestionCircle /> },
];

const CONTACT_METHODS = [
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone Call' },
];

const STATUS_LABELS = {
  open: 'Open',
  in_progress: 'In Progress',
  waiting_for_customer: 'Waiting for Customer',
  completed: 'Completed',
};

const STATUS_CLASSES = {
  open: 'status-open',
  in_progress: 'status-in-progress',
  waiting_for_customer: 'status-waiting',
  completed: 'status-completed',
};

const PRIORITY_CLASSES = {
  low: 'priority-low',
  medium: 'priority-medium',
  high: 'priority-high',
};

const EMPTY_FORM = {
  request_type: '',
  priority: '',
  subject: '',
  description: '',
  contact_method: 'whatsapp',
  contact_detail: '',
};

const Support = () => {
  const [stats, setStats] = useState({ open: 0, in_progress: 0, waiting_for_customer: 0, completed: 0, avg_response_hours: null });
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  const [formData, setFormData] = useState(EMPTY_FORM);
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);
  const formRef = useRef(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');

  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [ticketDetail, setTicketDetail] = useState(null);
  const [ticketUpdates, setTicketUpdates] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [replyMessage, setReplyMessage] = useState('');
  const [replySubmitting, setReplySubmitting] = useState(false);

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const response = await fetch('https://demo-site.creativepulse.pk/demo-api/admin/get-tickets.php');
      const data = await response.json();
      if (data.success) {
        setTickets(data.tickets || []);
        setStats(data.stats || stats);
      } else {
        toast.error(data.message || 'Failed to load tickets');
      }
    } catch (error) {
      console.error('Error fetching tickets:', error);
      toast.error('Connection error loading tickets');
    } finally {
      setLoading(false);
    }
  };

  const handleQuickRequest = (type) => {
    setFormData(prev => ({ ...prev, request_type: type }));
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleFormChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      toast.error('Only JPEG or PNG files are allowed');
      e.target.value = '';
      return;
    }
    setAttachmentFile(file);
  };

  const handleClearForm = () => {
    setFormData(EMPTY_FORM);
    setAttachmentFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.request_type || !formData.priority || !formData.subject || !formData.description || !formData.contact_detail) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const body = new FormData();
      Object.entries(formData).forEach(([key, value]) => body.append(key, value));
      if (attachmentFile) body.append('attachment', attachmentFile);

      const response = await fetch('https://demo-site.creativepulse.pk/demo-api/admin/create-ticket.php', {
        method: 'POST',
        body
      });
      const data = await response.json();

      if (data.success) {
        toast.success(`Request submitted! Ticket ${data.ticket_number}`);
        handleClearForm();
        fetchTickets();
      } else {
        toast.error(data.message || 'Failed to submit request');
      }
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('Connection error');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
  };

  const filteredTickets = useMemo(() => {
    return tickets.filter(t => {
      const term = searchTerm.trim().toLowerCase();
      const matchesSearch =
        !term ||
        (t.ticket_number || '').toLowerCase().includes(term) ||
        (t.subject || '').toLowerCase().includes(term) ||
        (t.request_type || '').toLowerCase().includes(term);

      const matchesStatus = statusFilter === 'all' || t.status === statusFilter;
      const matchesPriority = priorityFilter === 'all' || t.priority === priorityFilter;

      return matchesSearch && matchesStatus && matchesPriority;
    });
  }, [tickets, searchTerm, statusFilter, priorityFilter]);

  const handleResetFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setPriorityFilter('all');
  };

  const requestTypeLabel = (value) => REQUEST_TYPES.find(r => r.value === value)?.label || value;

  // --- Ticket detail drawer ---
  const openTicket = async (id) => {
    setSelectedTicketId(id);
    setDetailLoading(true);
    setTicketDetail(null);
    setTicketUpdates([]);
    try {
      const response = await fetch(`https://demo-site.creativepulse.pk/demo-api/admin/get-ticket-detail.php?id=${id}`);
      const data = await response.json();
      if (data.success) {
        setTicketDetail(data.ticket);
        setTicketUpdates(data.updates || []);
      } else {
        toast.error(data.message || 'Failed to load ticket');
      }
    } catch (error) {
      console.error('Error fetching ticket:', error);
      toast.error('Connection error');
    } finally {
      setDetailLoading(false);
    }
  };

  const closeTicket = () => {
    setSelectedTicketId(null);
    setTicketDetail(null);
    setTicketUpdates([]);
    setReplyMessage('');
  };

  const handleAddReply = async () => {
    if (!replyMessage.trim()) return;
    setReplySubmitting(true);
    try {
      const response = await fetch('https://demo-site.creativepulse.pk/demo-api/admin/add-ticket-reply.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ticket_id: selectedTicketId, message: replyMessage.trim() })
      });
      const data = await response.json();
      if (data.success) {
        setReplyMessage('');
        openTicket(selectedTicketId); // refresh conversation
        fetchTickets(); // refresh list (last update / status may have changed)
      } else {
        toast.error(data.message || 'Failed to send reply');
      }
    } catch (error) {
      console.error('Reply error:', error);
      toast.error('Connection error');
    } finally {
      setReplySubmitting(false);
    }
  };

  return (
    <div className="support-page">
      <div className="support-header">
        <div>
          <h1>Support</h1>
          <p>Request help, report issues, or ask our team to make changes to your store.</p>
          <p className="support-note">For urgent issues, please describe the problem clearly and attach screenshots where possible.</p>
        </div>
        <button className="new-request-btn" onClick={() => formRef.current?.scrollIntoView({ behavior: 'smooth' })}>
          <FaPlus /> New Support Request
        </button>
      </div>

      {/* STAT CARDS */}
      <div className="support-stats-grid">
        <div className="support-stat-card">
          <div className="support-stat-top">
            <div>
              <p className="support-stat-title">Open Tickets</p>
              <h3 className="support-stat-value">{loading ? '—' : stats.open}</h3>
            </div>
            <div className="support-stat-icon" style={{ background: '#dbeafe', color: '#1d4ed8' }}><FaInbox /></div>
          </div>
          <p className="support-stat-sub">Waiting for action</p>
        </div>
        <div className="support-stat-card">
          <div className="support-stat-top">
            <div>
              <p className="support-stat-title">In Progress</p>
              <h3 className="support-stat-value">{loading ? '—' : stats.in_progress}</h3>
            </div>
            <div className="support-stat-icon" style={{ background: '#fdf1d6', color: '#a3760a' }}><FaSpinner /></div>
          </div>
          <p className="support-stat-sub">Being handled</p>
        </div>
        <div className="support-stat-card">
          <div className="support-stat-top">
            <div>
              <p className="support-stat-title">Completed</p>
              <h3 className="support-stat-value">{loading ? '—' : stats.completed}</h3>
            </div>
            <div className="support-stat-icon" style={{ background: '#dcf5e3', color: '#1e8a4c' }}><FaCheckCircle /></div>
          </div>
          <p className="support-stat-sub">Resolved requests</p>
        </div>
        <div className="support-stat-card">
          <div className="support-stat-top">
            <div>
              <p className="support-stat-title">Average Response</p>
              {/* TODO: approximate — see get-tickets.php comment. Shows "—"
                  until at least one ticket has a team reply. */}
              <h3 className="support-stat-value">{loading || stats.avg_response_hours === null ? '—' : `${stats.avg_response_hours} hrs`}</h3>
            </div>
            <div className="support-stat-icon" style={{ background: '#fdece8', color: '#e8604c' }}><FaClock /></div>
          </div>
          <p className="support-stat-sub">Typical response time</p>
        </div>
      </div>

      {/* QUICK REQUESTS */}
      <div className="panel quick-requests-panel">
        <h2 className="panel-title-icon"><FaPlus className="title-icon" /> Quick Requests</h2>
        <p className="panel-sub">Choose a common request to fill the support form faster.</p>
        <div className="quick-requests-grid">
          {REQUEST_TYPES.map(rt => (
            <div key={rt.value} className="quick-request-card" onClick={() => handleQuickRequest(rt.value)}>
              <div className="quick-request-icon">{rt.icon}</div>
              <div>
                <strong>{rt.label}</strong>
                <p>{rt.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* SUBMIT FORM */}
      <div className="panel" ref={formRef}>
        <h2 className="panel-title">Submit a Support Request</h2>
        <p className="panel-sub">Share the details of your request so our team can help you faster.</p>

        <form onSubmit={handleSubmit} className="support-form">
          <div className="support-form-row">
            <div className="form-group">
              <label>Request Type *</label>
              <select name="request_type" value={formData.request_type} onChange={handleFormChange} required>
                <option value="">Select request type</option>
                {REQUEST_TYPES.map(rt => (
                  <option key={rt.value} value={rt.value}>{rt.label}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Priority *</label>
              <select name="priority" value={formData.priority} onChange={handleFormChange} required>
                <option value="">Select priority</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
              <small>Use High only for urgent issues affecting orders or checkout.</small>
            </div>
          </div>

          <div className="form-group">
            <label>Subject *</label>
            <input
              type="text"
              name="subject"
              placeholder="Example: Checkout button not working on mobile"
              value={formData.subject}
              onChange={handleFormChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Description *</label>
            <textarea
              name="description"
              rows="5"
              placeholder="Describe what you need help with. Include page names, product names, order IDs, screenshots, or examples where possible."
              value={formData.description}
              onChange={handleFormChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Attachment</label>
            <div className="attachment-row">
              <button type="button" className="choose-file-btn" onClick={() => fileInputRef.current?.click()}>
                <FaPaperclip /> Choose file
              </button>
              <span className="attachment-filename">{attachmentFile ? attachmentFile.name : 'No file selected'}</span>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
            </div>
            <small>Upload screenshots or files that help explain your request. JPEG or PNG only.</small>
          </div>

          <div className="support-form-row">
            <div className="form-group">
              <label>Preferred Contact Method</label>
              <select name="contact_method" value={formData.contact_method} onChange={handleFormChange}>
                {CONTACT_METHODS.map(cm => (
                  <option key={cm.value} value={cm.value}>{cm.label}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Contact Detail</label>
              <input
                type="text"
                name="contact_detail"
                placeholder="0300-1234567"
                value={formData.contact_detail}
                onChange={handleFormChange}
                required
              />
              <small>Our team may contact you here if more details are needed.</small>
            </div>
          </div>

          <div className="support-form-actions">
            <button type="button" className="clear-form-btn" onClick={handleClearForm}>
              Clear Form
            </button>
            <button type="submit" className="submit-request-btn" disabled={submitting}>
              <FaPaperPlane /> {submitting ? 'Submitting...' : 'Submit Request'}
            </button>
          </div>
        </form>
      </div>

      {/* TICKETS LIST */}
      <div className="panel">
        <h2 className="panel-title">Your Support Tickets</h2>
        <p className="panel-sub">Track your requests and see the latest updates from our team.</p>

        <div className="support-filter-bar">
          <div className="support-search">
            <FaSearch className="support-search-icon" />
            <input
              type="text"
              placeholder="Search by ticket ID, subject, or request type"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="support-filter-select">
            <option value="all">All Statuses</option>
            {Object.entries(STATUS_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
          <select value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)} className="support-filter-select">
            <option value="all">All Priorities</option>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
          </select>
          <button className="reset-filters-btn" onClick={handleResetFilters}>
            <FaSync />
          </button>
        </div>

        {loading ? (
          <div className="loader-container">
            <div className="loader"></div>
            <p>Loading tickets...</p>
          </div>
        ) : filteredTickets.length === 0 ? (
          <div className="no-tickets">
            <p>No support tickets found.</p>
          </div>
        ) : (
          <div className="support-table-container">
            <table className="support-table">
              <thead>
                <tr>
                  <th>Ticket ID</th>
                  <th>Request Type</th>
                  <th>Subject</th>
                  <th>Date</th>
                  <th>Priority</th>
                  <th>Status</th>
                  <th>Last Update</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredTickets.map(t => (
                  <tr key={t.id}>
                    <td className="ticket-id-cell">{t.ticket_number}</td>
                    <td>{requestTypeLabel(t.request_type)}</td>
                    <td>{t.subject}</td>
                    <td className="muted-cell">{formatDate(t.created_at)}</td>
                    <td><span className={`priority-pill ${PRIORITY_CLASSES[t.priority] || ''}`}>{t.priority?.charAt(0).toUpperCase() + t.priority?.slice(1)}</span></td>
                    <td><span className={`status-pill ${STATUS_CLASSES[t.status] || ''}`}>{STATUS_LABELS[t.status] || t.status}</span></td>
                    <td className="muted-cell">{t.last_update || '—'}</td>
                    <td>
                      <button className="view-ticket-btn" onClick={() => openTicket(t.id)}>
                        <FaEye /> View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* HOW TO GET FASTER SUPPORT (static) */}
      <div className="panel faster-support-panel">
        <h2 className="panel-title">How to Get Faster Support</h2>
        <p className="panel-sub">To help our team respond faster, include screenshots, order IDs, product names, page names, and a clear description of the issue.</p>
        <div className="faster-support-tips">
          <div className="tip-item">
            <span className="tip-number">1</span>
            <span>Mention the exact page or feature</span>
          </div>
          <div className="tip-item">
            <span className="tip-number">2</span>
            <span>Attach a screenshot if possible</span>
          </div>
          <div className="tip-item">
            <span className="tip-number">3</span>
            <span>Use High priority only for urgent order or checkout issues</span>
          </div>
        </div>
      </div>

      {/* TICKET DETAIL DRAWER */}
      {selectedTicketId && (
        <div className="ticket-drawer-overlay">
          <div className="ticket-drawer-backdrop" onClick={closeTicket}></div>
          <div className="ticket-drawer">
            {detailLoading || !ticketDetail ? (
              <div className="drawer-loading">
                <div className="loader"></div>
                <p>Loading ticket...</p>
              </div>
            ) : (
              <>
                <div className="ticket-drawer-header">
                  <div className="ticket-drawer-title-row">
                    <h2>{ticketDetail.ticket_number}</h2>
                    <span className={`status-pill ${STATUS_CLASSES[ticketDetail.status] || ''}`}>{STATUS_LABELS[ticketDetail.status] || ticketDetail.status}</span>
                    <span className={`priority-pill ${PRIORITY_CLASSES[ticketDetail.priority] || ''}`}>{ticketDetail.priority?.charAt(0).toUpperCase() + ticketDetail.priority?.slice(1)}</span>
                  </div>
                  <p className="ticket-drawer-type">{requestTypeLabel(ticketDetail.request_type)}</p>
                  <button className="drawer-close-btn" onClick={closeTicket}><FaTimes /></button>
                </div>

                <div className="ticket-drawer-body">
                  <div className="ticket-info-card">
                    <h3>{ticketDetail.subject}</h3>
                    <div className="ticket-info-grid">
                      <div>
                        <span className="ticket-info-label">Request Type</span>
                        <span>{requestTypeLabel(ticketDetail.request_type)}</span>
                      </div>
                      <div>
                        <span className="ticket-info-label">Priority</span>
                        <span>{ticketDetail.priority?.charAt(0).toUpperCase() + ticketDetail.priority?.slice(1)}</span>
                      </div>
                      <div>
                        <span className="ticket-info-label">Status</span>
                        <span>{STATUS_LABELS[ticketDetail.status] || ticketDetail.status}</span>
                      </div>
                      <div>
                        <span className="ticket-info-label">Created</span>
                        <span>{formatDate(ticketDetail.created_at)}</span>
                      </div>
                      <div>
                        <span className="ticket-info-label">Contact Method</span>
                        <span>{CONTACT_METHODS.find(c => c.value === ticketDetail.contact_method)?.label || ticketDetail.contact_method || '—'}</span>
                      </div>
                      <div>
                        <span className="ticket-info-label">Contact Detail</span>
                        <span>{ticketDetail.contact_detail || '—'}</span>
                      </div>
                    </div>

                    <div className="ticket-description">
                      <span className="ticket-info-label">Description</span>
                      <p>{ticketDetail.description}</p>
                    </div>

                    {ticketDetail.attachment && (
                      <div className="ticket-attachment">
                        <span className="ticket-info-label">Attachment</span>
                        <a href={ticketDetail.attachment} target="_blank" rel="noopener noreferrer">
                          <img src={ticketDetail.attachment} alt="Attachment" />
                        </a>
                      </div>
                    )}
                  </div>

                  <div className="conversation-section">
                    <h3>Conversation / Updates</h3>
                    <div className="conversation-thread">
                      {ticketUpdates.map(u => (
                        <div key={u.id} className={`conversation-item ${u.author_type === 'admin_team' ? 'from-team' : 'from-store-owner'}`}>
                          <div className="conversation-item-header">
                            <span className="conversation-author">{u.author_type === 'admin_team' ? 'Team' : 'Customer'}</span>
                            <span className="conversation-date">{formatDate(u.created_at)}</span>
                          </div>
                          <p>{u.message}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="add-reply-section">
                    <label>Add Reply</label>
                    <textarea
                      rows="3"
                      placeholder="Type your reply or add more details for our team..."
                      value={replyMessage}
                      onChange={(e) => setReplyMessage(e.target.value)}
                    />
                    <button
                      className="send-reply-btn"
                      onClick={handleAddReply}
                      disabled={replySubmitting || !replyMessage.trim()}
                    >
                      <FaPaperPlane /> {replySubmitting ? 'Sending...' : 'Send Reply'}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Support;
